<template>
  <b-card class="mb-3">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <h6 class="mb-0">
        <b v-if="productState === 'draft'">Draft report</b>
        <b v-else-if="productState === 'bespoke'">Bespoke report</b>
        <b v-else>Report: {{ reportNumber }}</b>
        <span class="ml-1">|</span>
        <span class="ml-1">Product Status: {{ displayProductStatus }}</span>
        <span
          v-if="isLoggedIn && isStandardProduct"
          id="add-to-portfolio-button-wrapper"
          class="d-inline-block"
          tabindex="0"
        >
          <b-button
            variant="outline-success"
            size="sm"
            class="ml-2"
            style="position: relative; top: -2px;"
            :disabled="!isPremium"
            :style="!isPremium ? 'pointer-events: none;' : ''"
            @click="$emit('add-to-portfolio')"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
              class="mr-50"
            />
            Add To Portfolio
            <feather-icon
              v-if="!isPremium"
              icon="LockIcon"
              size="14"
              class="ml-50"
            />
          </b-button>
        </span>
        <b-tooltip
          v-if="isLoggedIn && !isPremium"
          target="add-to-portfolio-button-wrapper"
          triggers="hover"
          placement="top"
        >
          {{ premiumFeatureTooltip }}
        </b-tooltip>
      </h6>
      <product-pdf-buttons
        :product-state="productState"
        :is-generating-pdf="isGeneratingPdf"
        @generate-pdf="$emit('generate-pdf', $event)"
      />
    </div>
    <p
      v-if="isLoggedIn && usersFullName"
      class="text-muted mb-1"
    >
      Generated for {{ usersFullName }}<template v-if="companyName">
        of {{ companyName }}
      </template> on {{ generatedDate }}
    </p>
    <h1 id="report-title" class="d-flex align-items-center">
      <b-avatar
        class="mr-1"
        :variant="productTypeIconColor"
        size="48"
        rounded="lg"
      >
        <feather-icon
          size="24"
          :icon="productTypeIcon"
        />
      </b-avatar>
      {{ reportTitle }}
    </h1>
    <b-row class="match-height mt-2 mb-2">
      <b-col
        cols="12"
        md="3"
      >
        <div class="d-flex justify-content-center align-items-start">
          <img
            class="mb-0 shadow"
            width="100"
            :src="`${baseURL}servethumbnail.cfm?id=${detailsId}`"
          >
        </div>
      </b-col>
      <b-col
        cols="12"
        md="9"
      >
        <div class="ml-1 mr-1">
          <b-row>
            <b-col cols="8">
              <b-row>
                <b-col
                  cols="6"
                  class="mb-2"
                >
                  <h4 class="mb-0 text-secondary font-weight-bold">
                    Provider
                  </h4>
                  <h3 class="mb-0">
                    <router-link
                      v-if="providerInformation.providerId"
                      :to="`/providers/provider-details/${providerInformation.providerId}`"
                    >{{ providerInformation.name }}</router-link>
                    <template v-else>{{ providerInformation.name }}</template>
                  </h3>
                </b-col>
                <b-col
                  cols="6"
                  class="mb-2"
                >
                  <h4 class="mb-0 text-secondary font-weight-bold">
                    Counterparty
                  </h4>
                  <h3 class="mb-0">
                    <router-link
                      v-if="keyInfo.bottomTbl['Counterparty'] && typeof keyInfo.bottomTbl['Counterparty'] === 'object'"
                      :to="`/data/counterparties/${keyInfo.bottomTbl['Counterparty'].COUNTERPARTYID}`"
                    >{{ keyInfo.bottomTbl['Counterparty'].NAME }}</router-link>
                    <template v-else>{{ keyInfo.bottomTbl['Counterparty'] }}</template>
                  </h3>
                </b-col>
                <b-col cols="6">
                  <h4 class="mb-0 text-secondary font-weight-bold">
                    Investment Length
                  </h4>
                  <h3 class="mb-0">
                    {{ keyInfo.leftTbl['Investment Length'] }}
                  </h3>
                </b-col>
                <b-col
                  v-if="identifierLabel"
                  cols="6"
                >
                  <h4 class="mb-0 text-secondary font-weight-bold">
                    {{ identifierLabel }}
                  </h4>
                  <h3 class="mb-0">
                    {{ keyInfo.bottomTbl[identifierLabel] }}
                  </h3>
                </b-col>
              </b-row>
            </b-col>
            <b-col cols="4">
              <h4 class="mb-0 text-secondary">
                Underlying Asset
              </h4>
              <h5 class="mb-0">
                <template v-if="Array.isArray(keyInfo.bottomTbl['Underlying Asset'])">
                  <span
                    v-for="(item, i) in keyInfo.bottomTbl['Underlying Asset']"
                    :key="i"
                  >
                    <router-link :to="`/data/underlyings/${item.UNDERLYINGCODE}`">{{ item.NAME }}</router-link><span v-if="i < keyInfo.bottomTbl['Underlying Asset'].length - 1">, </span>
                  </span>
                </template>
                <template v-else>{{ keyInfo.bottomTbl['Underlying Asset'] }}</template>
              </h5>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BRow, BCol, BCard, BAvatar, BButton, BTooltip,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import ProductPdfButtons from './ProductPdfButtons.vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BAvatar,
    BButton,
    BTooltip,
    ProductPdfButtons,
  },
  props: {
    reportNumber: {
      type: [String, Number],
      default: '',
    },
    reportTitle: {
      type: String,
      default: '',
    },
    productTypeIcon: {
      type: String,
      default: 'BoxIcon',
    },
    productTypeIconColor: {
      type: String,
      default: 'light-primary',
    },
    providerInformation: {
      type: Object,
      default: () => ({
        name: '',
      }),
    },
    keyInfo: {
      type: Object,
      default: () => ({
        leftTbl: {},
        bottomTbl: {},
      }),
    },
    topBox: {
      type: Object,
      default: () => ({}),
    },
    detailsId: {
      type: [String, Number],
      default: '',
    },
    productState: {
      type: String,
      default: 'current',
    },
    isGeneratingPdf: {
      type: Boolean,
      default: false,
    },
    reportStructureData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      baseURL: process.env.VUE_APP_BACKEND_PATH,
      generatedDate: new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isLoggedIn',
      'isPremium',
      'usersFullName',
    ]),
    companyName() {
      return this.$store.state.auth.user.companyname
    },
    identifierLabel() {
      if (this.keyInfo.bottomTbl.ISIN) return 'ISIN'
      if (this.keyInfo.bottomTbl['Identifier Reference']) return 'Identifier Reference'
      return ''
    },
    premiumFeatureTooltip() {
      if (!this.isLoggedIn) {
        return 'Upgrade to Premium to use this feature'
      }
      return 'Upgrade to Premium to use this feature'
    },
    displayProductStatus() {
      switch (this.productState) {
        case 'early_live':
        case 'live':
          return 'Live'
        default:
          return this.productState[0].toUpperCase() + this.productState.slice(1)
      }
    },
    isStandardProduct() {
      return !['draft', 'bespoke'].includes(String(this.productState).toLowerCase())
    },
  },
  methods: {},
}
</script>
